import { showToast } from "../Notifications/notificationsSlice";
import {api} from "../../api.ts";
import {unwrapData} from "../../apiResponse.ts";

export interface Queue {
    id: string | null;
    name: string;
    ringStrategy?: string;
    ivrMenuOption: Config;
    phoneNumbers?: PhoneNumber[];
}
export interface Action {
    order: string;
    type: string;
    userId?: number;
    groupId?: number;
    phoneNumberId?: number;
    messageSettings?: MessageFile;
    label?: any;
    phoneNumber?: string;
    keyName?: string;
}

interface MessageFile {
    originalFileName: string;
    bucketFileName: string;
}

interface MessageSettings {
    WELCOME_MESSAGE: MessageFile;
    MENU_MESSAGE: MessageFile;
}

interface Config {
    enabled: boolean;
    actions: Action[];
    messageSettings: MessageSettings;
    maxMenuRepeats: number;
}
interface CreateQueueRequest {
    name: string;
    ringStrategy?: string;
    ivrMenuOption: Config;
}
export interface PhoneNumber {
    id?: number;
    msisdnId: number;
    phoneNumber?: number;
    msisdn: number;
    label: string;
    icon: string;
    type: string;
    iconColor: string | null;
    assigmentScope?: 'PUBLIC' | 'PRIVATE';
    assignmentScope?: 'PUBLIC' | 'PRIVATE';
}
export interface CallQueueNumbers {
    id?: number,
    msisdnId?: number,
    phoneNumber?: number,
    assigmentScope?: string,
    assignmentScope?: string,
    msisdn: number,
    label: string,
    icon: string,
    type: string,
    iconColor: string | null
}
export interface QueuesNumbersResponse {
    queueId: number;
    numbers: PhoneNumber[];
}
export interface QueueNumbers {
    queuesNumbers: QueuesNumbersResponse[];
}


export interface QueueWithNumbers extends Queue {
    phoneNumbers: PhoneNumber[];
}
export const callQueueApi= api.injectEndpoints({
    endpoints: (builder) => ({

        getQueues: builder.query<Queue[], void>({
            query: () => ({
                url: '/api/v1/queues',
                method: 'GET',
            }),
            transformResponse: (response: { data: Queue[] }) => unwrapData(response),
            providesTags: ['Queue'],
        }),
        getQueuesNumbers: builder.query<QueueNumbers, void>({
            query: () => ({
                url: '/api/v1/queues/phone-numbers',
                method: 'GET',
            }),
            transformResponse: (response: QueueNumbers | { data: QueueNumbers }) => unwrapData(response),
            providesTags: ['Numbers'],
        }),
        getQueuesWithNumbers: builder.query<QueueWithNumbers[], void>({
            async queryFn(_arg, _, _extraOptions, fetchBaseQuery): Promise<any> {

                const queuesResponse = await fetchBaseQuery({
                    url: '/api/v1/queues',
                    method: 'GET'
                });

                const numbersResponse = await fetchBaseQuery({
                    url: '/api/v1/queues/phone-numbers',
                    method: 'GET'
                });

                if (queuesResponse.error) return { error: queuesResponse.error };
                if (numbersResponse.error) return { error: numbersResponse.error };

                const queuesData = unwrapData(queuesResponse.data as Queue[] | { data: Queue[] });
                const numbersData = unwrapData(numbersResponse.data as QueueNumbers | { data: QueueNumbers });

                const queues = queuesData || [];
                const queueNumbers = numbersData?.queuesNumbers || [];

                const numbersMap = new Map<string, PhoneNumber[]>();
                queueNumbers.forEach((qn: QueuesNumbersResponse) => {
                    const queueIdKey = String(qn.queueId);
                    numbersMap.set(queueIdKey, qn.numbers);
                });

                const queuesWithNumbers: QueueWithNumbers[] = queues.map((queue: Queue) => {
                    const queueIdKey = String(queue.id);
                    return {
                        ...queue,
                        phoneNumbers: numbersMap.get(queueIdKey) || []
                    };
                });

                return { data: queuesWithNumbers };
            },
            providesTags: ['Queue', 'Numbers'],
        }),
        getQueueById: builder.query<Queue, number>({
            query: (id) => ({
                url: `/api/v1/queues/${id}`,
                method: 'GET',
            }),
            transformResponse: (response: Queue | { data: Queue }) => unwrapData(response),
            providesTags: (_result, _error, id) => [{ type: 'Queue', id }],
        }),

        createQueue: builder.mutation<Queue, CreateQueueRequest>({
            query: (data) => ({
                url: '/api/v1/queues',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response: Queue | { data: Queue }) => unwrapData(response),
            invalidatesTags: ['Queue', 'Numbers'],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({ description: 'Call queue created successfully!', type: 'success' }));
                } catch {
                    dispatch(showToast({ description: 'Failed to create call queue!', type: 'error' }));
                }
            },
        }),

        updateQueue: builder.mutation<Queue, { id: number; data: Partial<CreateQueueRequest> }>({
            query: ({ id, data }) => ({
                url: `/api/v1/queues/${id}`,
                method: 'PATCH',
                body: data,
            }),
            transformResponse: (response: Queue | { data: Queue }) => unwrapData(response),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'Queue', id }, 'Queue', 'Numbers'],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(showToast({description: 'Call queue updated successfully!', type: 'success' }));
                } catch {
                    dispatch(showToast({ description: 'Failed to update call queue!', type: 'error' }));
                }
            },

        }),
        uploadCallQueueSynthetizeText: builder.mutation<any, { text: string, gender: string, language: string }>({
            query: ({text, gender, language}) => ({
                url: `/api/v1/tts-speech`,
                method: 'PUT',
                body: {text, gender, language},
            }),
        }),
        deleteQueue: builder.mutation<{ success: boolean }, number>({
            query: (id) => ({
                url: `/api/v1/queues/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, id) => [{ type: 'Queue', id }, 'Queue', 'Numbers'],
        }),
        getQueuePhoneNumbers: builder.query<any, number>({
            query: (id) => ({
                url: `/api/v1/queues/${id}/phone-numbers`,
                method: 'GET',
            }),
            transformResponse: (response: any) => unwrapData(response),
            providesTags: (_result, _error, id) => [{ type: 'Numbers', id }],

        }),
        postQueuePhoneNumbers: builder.mutation<{ success: boolean }, {id: number, phoneNumbers: number[]}>({
            query: ({id, phoneNumbers}) => ({
                url: `/api/v1/queues/${id}/phone-numbers`,
                body: {phoneNumbers},
                method: 'POST',
            }),
            transformResponse: (response: { success: boolean } | { data: { success: boolean } }) => unwrapData(response),
            invalidatesTags: (_result, _error, arg) => [{ type: 'Numbers', id: arg.id },  'Queue', 'Numbers'],
        }),
        postCallQueueRecordingPreview: builder.mutation<any, { id?: string; data: FormData; audioURL: string }>({
            query: ({id, data}) => ({
                url: `/api/v1/files`,
                method: 'POST',
                body: data,
                params: {id},
            }),
            transformResponse: (response: any, _meta: any, arg: { id: string; data: FormData; audioURL: string }) => {
                const file = unwrapData(response);
                return {
                    ...file,
                    audioURL: arg.audioURL,
                };
            },
        }),
        getCallQueueRecording: builder.query<{ data: string; type: string }, { id: string | undefined; type: string }>({
            query: ({ id, type }) => ({
                url: `/api/v1/queues/${id}/message`,
                params: { type },
                responseHandler: async (response: any) => {
                    const arrayBuffer = await response.arrayBuffer();
                    return {
                        buffer: arrayBuffer,
                        headers: response.headers,
                    };
                },
            }),
            transformResponse: (response: { buffer: ArrayBuffer; headers: Headers }) => {
                const contentType = response.headers.get('content-type') || 'audio/mpeg';
                const blob = new Blob([response.buffer], { type: contentType });
                const audioUrl = URL.createObjectURL(blob);

                return {
                    data: audioUrl,
                    type: contentType,
                };
            },
            keepUnusedDataFor: 0,
        }),
        getPlayMessage: builder.query<{ data: string; type: string }, { id: string | undefined; order: number }>({
            query: ({ id, order }) => ({
                url: `/api/v1/queues/${id}/message?type=PLAY_MESSAGE_OPTION_${order}`,
                responseHandler: async (response: Response) => {
                    const arrayBuffer = await response.arrayBuffer();
                    return {
                        buffer: arrayBuffer,
                        headers: response.headers,
                    };
                },
            }),
            transformResponse: (response: { buffer: ArrayBuffer; headers: Headers }) => {
                if (!response.buffer || !response.headers) {
                    throw new Error('Invalid response structure');
                }
                const contentType = response.headers.get('content-type') || 'audio/mpeg';
                const blob = new Blob([response.buffer], { type: contentType });
                const audioUrl = URL.createObjectURL(blob);

                return {
                    data: audioUrl,
                    type: contentType,
                };
            },
            keepUnusedDataFor: 0,
        }),
    }),
});

export const {
    useUploadCallQueueSynthetizeTextMutation,
    usePostCallQueueRecordingPreviewMutation,
    useGetQueuesQuery,
    useGetQueuesWithNumbersQuery,
    useGetQueueByIdQuery,
    useCreateQueueMutation,
    useUpdateQueueMutation,
    useDeleteQueueMutation,
     useGetQueuePhoneNumbersQuery,
    useGetCallQueueRecordingQuery,
    useLazyGetPlayMessageQuery,
    usePostQueuePhoneNumbersMutation,
} = callQueueApi;
